import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import useLoadingContext from '../../../hooks/useLoadingContext'
import { useLoadingIds } from '../../../hooks/useLoadingIds'
import { useErrorSnackbar } from '../../../hooks/useErrorSnackbar'
import { Region, regionsApi } from '../../../api/swagger'
import CheckboxListCard, { CheckboxItems } from '../../Card/CheckboxListCard'
import { useMountEffect } from '../../../hooks/useMountEffect'
import { useNotificationContext } from '../../Context/NotificationContext'

export const NotificationRegionsCard: React.FC<{
  isEditMode?: boolean
}> = ({ isEditMode }) => {
  const { t } = useTranslation()
  const {
    Notification: { fetchRegions: fetchRegionsLoadingId },
  } = useLoadingIds()
  const { handleError } = useErrorSnackbar()
  const [regions, setRegions] = useState<Region[] | undefined>()
  const {
    notificationDetails,
    updateNotificationDetails,
    updateFormValidity,
    formValidity,
  } = useNotificationContext()
  const { regionKeys } = notificationDetails

  /** If a key is in this map, it's selected */
  const [regionKeysMap, setRegionKeysMap] = useState(() => {
    return new Map(regionKeys?.map((key) => [key, `${key}`]))
  })

  // We could probably exclude this if regions doesn't load but it does not matter if we do or don't
  const AllRegionsCheckboxItem = useRef<CheckboxItems>({
    name: t(
      'Notifications.NotificationRegionsCard.Checkbox.AllRegions',
      'All Regions'
    ),
    key: -1,
    checked: regionKeysMap.has(-1),
  })

  useEffect(() => {
    if (!!notificationDetails.roleKeys && regionKeysMap.size === 0) {
      setRegionKeysMap(new Map(regionKeys?.map((key) => [key, `${key}`])))
    }
  }, [notificationDetails, regionKeys, regionKeysMap.size])

  const fetchRegions = async () => {
    try {
      const { regions: fetchedRegions } = await regionsApi.fetchRegions({})
      setRegions(fetchedRegions)
    } catch (e) {
      await handleError(
        e,
        t(
          'Admin.NotificationRegionsCard.FetchRegions.FailedToFetch',
          'An unknown error occurred fetching regions.'
        )
      )
    }
  }

  const [checkboxItems, setCheckboxItems] = useState<CheckboxItems[]>([])

  useEffect(() => {
    setCheckboxItems([
      ...(regions ?? []).map(({ regionsKey, name }) => {
        return {
          name,
          key: regionsKey,
          checked: regionKeysMap.has(regionsKey),
        }
      }),
    ].sort((a,b) => (a.name < b.name ? -1 : a.name > b.name ? 1 : 0)))
  }, [regionKeysMap, regions ])

  const { triggerFetch } = useLoadingContext({
    asyncFunction: fetchRegions,
    loadingId: fetchRegionsLoadingId,
  })

  useMountEffect(() => {
    triggerFetch()
  })

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target

    if (regionKeysMap.has(+value)) {
      regionKeysMap.delete(+value)
    } else {
      regionKeysMap.set(+value, value)
    }
    setCheckboxItems([
      ...(regions ?? []).map(({ regionsKey, name }) => {
        return {
          name,
          key: regionsKey,
          checked: value === `-1` ? true : regionKeysMap.has(regionsKey),
        }
      }),
    ].sort((a,b) => (a.name < b.name ? -1 : a.name > b.name ? 1 : 0)))
    if (value === `-1`) {
      AllRegionsCheckboxItem.current = {
        ...AllRegionsCheckboxItem.current,
        checked: !AllRegionsCheckboxItem.current.checked,
      }

      const newMap = AllRegionsCheckboxItem.current.checked
        ? new Map(
            (regions ?? []).map(({ regionsKey }) => [
              regionsKey,
              `${regionsKey}`,
            ])
          )
        : new Map()

      setRegionKeysMap(newMap)
      updateNotificationDetails({
        ...notificationDetails,
        regionKeys: Array.from(newMap.keys()),
      })
    } else {
      setRegionKeysMap(regionKeysMap)
      updateNotificationDetails({
        ...notificationDetails,
        regionKeys: Array.from(regionKeysMap.keys()),
      })
    }
    updateFormValidity({
      ...formValidity,
      // Technically we can provide 0 length but it makes validation harder.
      regions: {
        input: true,
      },
    })
  }

  return (
    <CheckboxListCard
      checkboxItems={checkboxItems}
      headerName={t(
        'Admin.Notifications.NotificationRegionsCard.Header',
        'Regions'
      )}
      headerId={'notification-regions-header'}
      isFieldDisabled={!isEditMode}
      alertMessage={
        !formValidity.regions.input
          ? t(
              'Admin.Notifications.NotificationRegionsCard.RegionsValidation',
              'Please select one or more regions.'
            )
          : undefined
      }
      handleCheckboxChange={handleCheckboxChange}
      headerCheckbox={AllRegionsCheckboxItem.current}
    />
  )
}
